import type { JSX } from "react";
import { cva } from "class-variance-authority";

const categoryBadgeVariants = cva(
  "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide transition-colors",
  {
    variants: {
      category: {
        Training: "bg-blue-600 text-blue-100",
        Nutrition: "bg-green-600 text-green-100",
        Lifestyle: "bg-purple-600 text-purple-100",
        default: "bg-indigo-600 text-indigo-100",
      },
      variant: {
        default: "",
        subtle: "backdrop-blur-sm",
        // Tinted-outline pill for plain white/light backgrounds.
        tint: "border",
        // Frosted glass pill for badges sitting on photos.
        overlay: "border backdrop-blur-md shadow-sm",
      },
    },
    compoundVariants: [
      {
        category: "Training",
        variant: "subtle",
        class: "bg-blue-600/70 text-blue-50",
      },
      {
        category: "Nutrition",
        variant: "subtle",
        class: "bg-green-600/70 text-green-50",
      },
      {
        category: "Lifestyle",
        variant: "subtle",
        class: "bg-purple-600/70 text-purple-50",
      },
      {
        category: "default",
        variant: "subtle",
        class: "bg-indigo-600/70 text-indigo-50",
      },
      {
        category: "Training",
        variant: "tint",
        class: "bg-blue-600/10 text-blue-700 border-blue-600/30",
      },
      {
        category: "Nutrition",
        variant: "tint",
        class: "bg-green-600/10 text-green-700 border-green-600/30",
      },
      {
        category: "Lifestyle",
        variant: "tint",
        class: "bg-purple-600/10 text-purple-700 border-purple-600/30",
      },
      {
        category: "default",
        variant: "tint",
        class: "bg-indigo-600/10 text-indigo-700 border-indigo-600/30",
      },
      {
        category: "Training",
        variant: "overlay",
        class: "bg-white/85 text-blue-700 border-white/50",
      },
      {
        category: "Nutrition",
        variant: "overlay",
        class: "bg-white/85 text-green-700 border-white/50",
      },
      {
        category: "Lifestyle",
        variant: "overlay",
        class: "bg-white/85 text-purple-700 border-white/50",
      },
      {
        category: "default",
        variant: "overlay",
        class: "bg-white/85 text-indigo-700 border-white/50",
      },
    ],
    defaultVariants: {
      category: "default",
      variant: "default",
    },
  }
);

interface CategoryBadgeProps {
  category?: string;
  className?: string;
  variant?: "default" | "subtle" | "tint" | "overlay";
}

export function CategoryBadge({ category, className, variant = "default" }: CategoryBadgeProps): JSX.Element {
  // Map the category string to the variant
  const categoryVariant = (category === "Training" || category === "Nutrition" || category === "Lifestyle")
    ? category
    : "default";

  return (
    <span className={categoryBadgeVariants({ category: categoryVariant, variant, className })}>
      {category}
    </span>
  );
}
